import { useCallback, useEffect, useState } from 'react';
import { useDashboard } from '../../../context/dashboardContext';
import SawTable from './components/sawTable';
import CreateSaw from './components/createSaw';
import UpdateSaw from './components/updateSaw';
import ConfirmModal from '../../../components/shareable/confirmModal';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import CustomerPagination from '../../../components/shareable/customerPagination';
import toast from 'react-hot-toast';
import { useSearch } from '../../../context/SearchContext';

const Saw = () => {
  const { createSaw, updateSaw, deleteSaw, getMultipleData } = useDashboard();
  const { searchQuery, updateSearch } = useSearch(); // Get search context
  const [ModalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectRow, setSelectRow] = useState([]);
  const [updateRow, setUpdateRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [allSaws, setAllSaws] = useState([]);
  const [filteredSaws, setFilteredSaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  // Set the page in search context when component mounts
  useEffect(() => {
    updateSearch('', 'saw');
    
    // Clear search when component unmounts
    return () => updateSearch('', '');
  }, [updateSearch]);

  // Filter saws based on search query
  const filterSaws = useCallback((saws, query) => {
    if (!query.trim()) return saws;
    
    const searchTerm = query.toLowerCase().trim();
    return saws.filter(saw => {
      return (
        (saw.brand?.toLowerCase() || '').includes(searchTerm) ||
        (saw.model?.toLowerCase() || '').includes(searchTerm)
      );
    });
  }, []);

  const fetchSaws = useCallback(async () => {
    const res = await getMultipleData(["saws"]);
    if (res.success) {
      // Sort saws by model number in alphabetical order (A-Z)
      const sortedSaws = [...res.data].sort((a, b) => {
        const aModel = (a.model || '').toLowerCase();
        const bModel = (b.model || '').toLowerCase();
        return aModel.localeCompare(bModel);
      });
      
      setAllSaws(sortedSaws);
      
      // Apply search filter if there's a query
      const filtered = filterSaws(sortedSaws, searchQuery);
      setFilteredSaws(filtered);
      setLoading(false);
    }
  }, [getMultipleData, searchQuery, filterSaws]);

  useEffect(() => { fetchSaws(); }, [fetchSaws]);

  // Apply search filter when search query changes
  useEffect(() => {
    if (allSaws.length > 0) {
      const filtered = filterSaws(allSaws, searchQuery);
      setFilteredSaws(filtered);
      setCurrentPage(1); // Reset to first page when search changes
    }
  }, [searchQuery, allSaws, filterSaws]);

  const handleCreate = async (sawData) => {
    setLoading(true);
    // Extract files from formData
    const { image, pdf, ...restData } = sawData;
    
    const res = await createSaw(restData, image, pdf);
    setLoading(false);
    
    if (res.success) {
      toast.success('Product has been created');
      fetchSaws();
    } else {
      toast.error('Product can not be created: ' + (res.error || ''));
    }
  };

  const handleUpdate = async (sawData) => {
    setLoading(true);
    // Extract files from formData
    const { image, pdf, ...restData } = sawData;
    const res = await updateSaw(updateRow._id, restData, image, pdf);
    setLoading(false);
    
    if (res.success) {
      toast.success('Product has been updated');
      fetchSaws();
      setUpdateModalOpen(false);
    } else {
      toast.error('Product can not be updated: ' + (res.error || ''));
    }
  };

  const selectedRow = (row) => {
    setDeleteOpen(true);
    setSelectRow(row);
  };

  const selectedRowForUpdate = (row) => {
    setUpdateRow(row);
    setUpdateModalOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
  };

  const closeUpdate = () => {
    setUpdateModalOpen(false);
    setUpdateRow(null);
  };
  
  const onConfirmingDelete = async () => {
    setLoading(true);
    const res = await deleteSaw(selectRow._id);
    setLoading(false);
    setDeleteOpen(false);
    if (res.success) {
      toast.success('Product has been deleted')
      fetchSaws();
    } else {
      toast.error('Product can not be deleted')
    }
  };

  // Pagination logic - use filteredSaws instead of all saws
  const totalPages = Math.ceil(filteredSaws.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSaws = filteredSaws.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) { setCurrentPage(page); }
  };

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-4">
      <div className="relative mb-4 mt-3 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Saws {searchQuery && `(Filtered: ${filteredSaws.length} found)`}
        </h2>
        <button onClick={() => setModalOpen(true)} className="mt-3 sm:mt-0 px-4 py-2 text-sm font-medium bg-gradient-to-r from-red-600 to-red-800 text-white rounded-lg shadow hover:shadow-md hover:scale-[1.02] transition-all duration-200" >
          Add Saw
        </button>
      </div>

      <SawTable saws={currentSaws} currentPage={currentPage} onDelete={selectedRow} onUpdate={selectedRowForUpdate} />
      
      <CustomerPagination totalPages={totalPages} currentPage={currentPage} handlePageChange={handlePageChange} totalItems={filteredSaws.length} itemsPerPage={itemsPerPage} />

      <CreateSaw Update='Add New Saw' isOpen={ModalOpen} onClose={() => setModalOpen(false)} onSave={handleCreate} />
      
      <UpdateSaw Update='Update Saw' isOpen={updateModalOpen} onClose={closeUpdate} onSave={handleUpdate} sawData={updateRow} />
      
      <ConfirmModal onConfirm={onConfirmingDelete} title="Delete Saw" description="Confirm to delete Saw" isOpen={deleteOpen} onClose={closeDelete} />
    </div>
  );
};

export default Saw;
